import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTrendingHands } from '../api/hands';
import { API_ORIGIN } from '../api/client';
import Spinner from '../components/common/Spinner';

type Period = 'day' | 'week' | 'month' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  day: '今日',
  week: '今週',
  month: '今月',
  all: '全期間',
};

const PAGE_SIZE = 100;

export default function TrendingPage() {
  const [period, setPeriod] = useState<Period>('week');
  const [page, setPage] = useState(1);

  const { data: hands, isLoading } = useQuery({
    queryKey: ['trending', period, page],
    queryFn: () => getTrendingHands(period, page),
  });

  const changePeriod = (p: Period) => {
    setPeriod(p);
    setPage(1);
  };

  const hasNextPage = (hands?.length ?? 0) === PAGE_SIZE;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">トレンドランキング</h1>
        <div className="flex gap-1">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => changePeriod(p)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                period === p
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !hands || hands.length === 0 ? (
        <div className="text-center text-gray-500 py-12 border rounded-xl">
          この期間のランキングデータがありません
        </div>
      ) : (
        <>
        <div className="space-y-2">
          {hands.map((hand, index) => {
            const globalIndex = (page - 1) * PAGE_SIZE + index;
            const imageUrl = hand.source_image_url
              ? hand.source_image_url.startsWith('/')
                ? `${API_ORIGIN}${hand.source_image_url}`
                : hand.source_image_url
              : null;

            return (
              <Link
                key={hand.id}
                to={`/hands/${hand.id}`}
                className="flex items-center gap-3 p-3 border rounded-xl bg-white hover:bg-gray-50 transition-colors"
              >
                {/* Rank badge */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                    ${globalIndex === 0 ? 'bg-yellow-400 text-yellow-900' :
                      globalIndex === 1 ? 'bg-gray-300 text-gray-700' :
                      globalIndex === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-100 text-gray-600'}`}
                >
                  {globalIndex + 1}
                </div>

                {/* Thumbnail */}
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="w-14 h-10 object-cover rounded border shrink-0"
                  />
                ) : (
                  <div className="w-14 h-10 bg-gray-100 rounded border shrink-0 flex items-center justify-center text-gray-400 text-xs">
                    画像なし
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {hand.title ?? '無題の局面'}
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                    {hand.user && <span>@{hand.user.x_username}</span>}
                    <span>·</span>
                    <span className="text-green-600 font-medium">{hand.vote_count}票</span>
                    {hand.favorite_count > 0 && (
                      <>
                        <span>·</span>
                        <span>★{hand.favorite_count}</span>
                      </>
                    )}
                    <span>·</span>
                    <span>{new Date(hand.created_at).toLocaleDateString('ja-JP')}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* ページネーション */}
        {(page > 1 || hasNextPage) && (
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
              className="px-4 py-2 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              ← 前のページ
            </button>
            <span className="text-sm text-gray-500">{page}ページ</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNextPage}
              className="px-4 py-2 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              次のページ →
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
